// src/pages/admin/tabs/AppointmentsSettingsTab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Admin screen for the WhatsApp video-call booking config:
//   1. Booking Settings form  -> settings/appointment_settings (server-owned)
//   2. Holidays & Blocked Dates -> appointment_blocked_dates CRUD
// Reads/writes go through the products-app /api/video-call/* routes with the
// caller's Firebase ID token (same pattern as NotificationsTab). Writes are
// restricted to super admins server-side.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { B, fonts, Spinner, Btn } from "../../../components/UI";

const PRODUCTS_API_URL =
  process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";
const API = (p) => `${PRODUCTS_API_URL}/api/video-call${p}`;

const DAY_ORDER = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const DAY_LABELS = {
  MON: "Monday", TUE: "Tuesday", WED: "Wednesday", THU: "Thursday",
  FRI: "Friday", SAT: "Saturday", SUN: "Sunday",
};

const inputStyle = {
  width: "100%", padding: "9px 12px", fontSize: 13, fontWeight: 500,
  fontFamily: fonts.body, border: `1.5px solid ${B.border}`,
  borderRadius: 8, outline: "none", background: B.white, color: B.text,
  boxSizing: "border-box",
};

const card = (extra = {}) => ({
  background: B.white, border: `1px solid ${B.borderLight}`,
  borderRadius: 14, padding: "16px 20px", ...extra,
});

const label = (text) => (
  <div style={{ fontSize: 12, fontWeight: 600, color: B.textMid, marginBottom: 4, fontFamily: fonts.body }}>
    {text}
  </div>
);

// Turn flat weekly_hours object into an ordered array so the form can render
// one row per weekday. Absent days default to closed.
const buildWeekly = (wh) => {
  const src = wh && typeof wh === "object" ? wh : {};
  return DAY_ORDER.map((key) => {
    const s = src[key] || {};
    return {
      key,
      enabled: !!s.enabled,
      start: s.start_time || "10:00",
      end: s.end_time || "19:00",
    };
  });
};

const blankBlock = () => ({
  date: "",
  reason: "",
  mode: "full_day",
  block_start_time: "10:00",
  block_end_time: "14:00",
  recurring: "none",
});

export default function AppointmentsSettingsTab() {
  const { user, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // Settings form state.
  const [enabled, setEnabled] = useState(true);
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  const [slotDur, setSlotDur] = useState("30");
  const [buffer, setBuffer] = useState("10");
  const [minAdvance, setMinAdvance] = useState("120");
  const [maxAdvanceDays, setMaxAdvanceDays] = useState("10");
  const [maxPerSlot, setMaxPerSlot] = useState("1");
  const [weekly, setWeekly] = useState(buildWeekly(null));
  const [breaks, setBreaks] = useState([]);
  const [remindersStr, setRemindersStr] = useState("1440, 60, 10");

  // Blocked dates state.
  const [blocks, setBlocks] = useState([]);
  const [blockForm, setBlockForm] = useState(blankBlock());
  const [blockSaving, setBlockSaving] = useState(false);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3500); };

  const getToken = async () => {
    if (!user) throw new Error("Not signed in.");
    return user.getIdToken();
  };

  const loadBlocks = async (token) => {
    const res = await fetch(API("/blocked-dates"), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error("Failed to load blocked dates.");
    const json = await res.json();
    setBlocks(json?.data || []);
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(API("/settings"), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed to load settings.");
      const d = (await res.json()).data;

      setEnabled(!!d.enabled);
      setTimezone(d.timezone || "Asia/Kolkata");
      setSlotDur(String(d.slot_duration_minutes));
      setBuffer(String(d.buffer_minutes));
      setMinAdvance(String(d.min_advance_minutes));
      setMaxAdvanceDays(String(d.max_advance_days));
      setMaxPerSlot(String(d.max_bookings_per_slot));
      setWeekly(buildWeekly(d.weekly_hours));
      setBreaks((d.breaks || []).map((b) => ({ start: b.start, end: b.end })));
      setRemindersStr((d.reminders_minutes_before || []).join(", "));

      await loadBlocks(token);
    } catch (e) {
      setError(e.message || "Could not load settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const updateDay = (key, patch) => setWeekly(weekly.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const updateBreak = (idx, patch) => setBreaks(breaks.map((b, i) => (i === idx ? { ...b, ...patch } : b)));

  const saveSettings = async () => {
    if (!isSuperAdmin || saving) return;
    setSaving(true);
    setError("");
    try {
      const reminders = remindersStr
        .split(",")
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isInteger(n) && n > 0);

      const weekly_hours = {};
      for (const d of weekly) weekly_hours[d.key] = { enabled: d.enabled, start_time: d.start, end_time: d.end };

      const token = await getToken();
      const res = await fetch(API("/settings"), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          enabled,
          timezone: timezone.trim(),
          slot_duration_minutes: Number(slotDur),
          buffer_minutes: Number(buffer),
          min_advance_minutes: Number(minAdvance),
          max_advance_days: Number(maxAdvanceDays),
          max_bookings_per_slot: Number(maxPerSlot),
          weekly_hours,
          breaks,
          reminders_minutes_before: reminders,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError((json.errors && json.errors.join("; ")) || json.error || "Save failed.");
        return;
      }
      flash("Booking settings saved.");
    } catch (e) {
      setError(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const addBlock = async () => {
    if (!isSuperAdmin || blockSaving) return;
    setBlockSaving(true);
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(API("/blocked-dates"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(blockForm),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError((json.errors && json.errors.join("; ")) || json.error || "Failed to add.");
        return;
      }
      setBlockForm(blankBlock());
      await loadBlocks(token);
      flash("Blocked date added.");
    } catch (e) {
      setError(e.message || "Failed to add.");
    } finally {
      setBlockSaving(false);
    }
  };

  const toggleBlock = async (b) => {
    if (!isSuperAdmin) return;
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(API(`/blocked-dates/${b.date}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_active: !b.is_active }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.error || "Update failed.");
        return;
      }
      await loadBlocks(token);
      flash(b.is_active ? "Block deactivated." : "Block activated.");
    } catch (e) {
      setError(e.message || "Update failed.");
    }
  };

  const deleteBlock = async (b) => {
    if (!isSuperAdmin) return;
    setError("");
    try {
      const token = await getToken();
      const res = await fetch(API(`/blocked-dates/${b.date}`), {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.error || "Delete failed.");
        return;
      }
      await loadBlocks(token);
      flash("Blocked date deleted.");
    } catch (e) {
      setError(e.message || "Delete failed.");
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "30px 24px", fontFamily: fonts.body, color: B.textMid }}>
        <Spinner /> <span style={{ marginLeft: 8 }}>Loading appointment settings…</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 760 }}>

      {/* Status banners */}
      {notice && (
        <div style={{ background: B.greenLight, color: B.green, border: `1px solid ${B.green}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body }}>
          ✓ {notice}
        </div>
      )}
      {error && (
        <div style={{ background: B.redLight, color: B.red, border: `1px solid ${B.red}`, borderRadius: 10, padding: "10px 14px", fontSize: 13, fontFamily: fonts.body }}>
          {error}
        </div>
      )}
      {!isSuperAdmin && (
        <div style={{ ...card(), borderLeft: `4px solid ${B.orangeMid}` }}>
          <span style={{ fontSize: 13, fontFamily: fonts.body, color: B.textMid }}>
            You have <strong>view-only</strong> access. Only a super admin can save booking settings or manage holiday blocks.
          </span>
        </div>
      )}

      {/* ─── Booking settings ─── */}
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: B.text, fontFamily: fonts.adminDisplay }}>Booking Settings</h3>

      <div style={card()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: B.orange }} />
          <label style={{ fontSize: 14, fontWeight: 700, color: B.text, fontFamily: fonts.body }}>
            Booking is enabled on WhatsApp
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div>{label("Timezone")}
            <input style={inputStyle} value={timezone} onChange={(e) => setTimezone(e.target.value)} />
          </div>
          <div>{label("Slot length (min)")}
            <input style={inputStyle} type="number" min="5" value={slotDur} onChange={(e) => setSlotDur(e.target.value)} />
          </div>
          <div>{label("Buffer (min)")}
            <input style={inputStyle} type="number" min="0" value={buffer} onChange={(e) => setBuffer(e.target.value)} />
          </div>
          <div>{label("Min advance (min)")}
            <input style={inputStyle} type="number" min="0" value={minAdvance} onChange={(e) => setMinAdvance(e.target.value)} />
          </div>
          <div>{label("Max advance (days)")}
            <input style={inputStyle} type="number" min="1" value={maxAdvanceDays} onChange={(e) => setMaxAdvanceDays(e.target.value)} />
          </div>
          <div>{label("Bookings per slot")}
            <input style={inputStyle} type="number" min="1" value={maxPerSlot} onChange={(e) => setMaxPerSlot(e.target.value)} />
          </div>
        </div>

        <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: B.textMid, fontFamily: fonts.body }}>Reminders (min before, comma-separated):</label>
          <input style={{ ...inputStyle, width: 260 }} value={remindersStr} onChange={(e) => setRemindersStr(e.target.value)} />
        </div>
      </div>

      <div style={card()}>
        <h4 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: B.text, fontFamily: fonts.adminDisplay }}>Weekly Hours</h4>
        <p style={{ margin: "0 0 14px", fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>
          Toggle a day to open/close it, then set its opening and closing time. Un-ticked days are closed.
        </p>
        <div style={{ display: "grid", gap: 10 }}>
          {weekly.map((d) => (
            <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 14, background: B.cream, borderRadius: 10, padding: "10px 14px" }}>
              <input type="checkbox" checked={d.enabled} onChange={(e) => updateDay(d.key, { enabled: e.target.checked })}
                style={{ width: 17, height: 17, accentColor: B.orange }} />
              <span style={{ width: 110, fontWeight: 700, fontSize: 13, color: d.enabled ? B.text : B.textLight, fontFamily: fonts.body }}>
                {DAY_LABELS[d.key]}
              </span>
              {d.enabled ? (
                <>
                  <input style={{ width: 110 }} type="time" value={d.start} onChange={(e) => updateDay(d.key, { start: e.target.value })} />
                  <span style={{ color: B.textLight }}>–</span>
                  <input style={{ width: 110 }} type="time" value={d.end} onChange={(e) => updateDay(d.key, { end: e.target.value })} />
                </>
              ) : (
                <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Closed</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div style={card()}>
        <h4 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: B.text, fontFamily: fonts.adminDisplay }}>Breaks</h4>
        {breaks.length === 0 && <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>No breaks — the shop is open straight through each open day.</p>}
        {breaks.map((b, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
            <input style={{ width: 120 }} type="time" value={b.start} onChange={(e) => updateBreak(i, { start: e.target.value })} />
            <span style={{ color: B.textLight }}>–</span>
            <input style={{ width: 120 }} type="time" value={b.end} onChange={(e) => updateBreak(i, { end: e.target.value })} />
            <button type="button" onClick={() => setBreaks(breaks.filter((_, idx) => idx !== i))}
              style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16 }} title="Remove break">🗑️</button>
          </div>
        ))}
        <Btn variant="ghost" small onClick={() => setBreaks([...breaks, { start: "13:00", end: "14:00" }])}>+ Add break</Btn>
      </div>

      <Btn variant="primary" fullWidth disabled={!isSuperAdmin || saving} onClick={saveSettings}>
        {saving ? "Saving…" : "Save Booking Settings"}
      </Btn>

      {/* ─── Holidays & blocked dates ─── */}
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: B.text, fontFamily: fonts.adminDisplay }}>Holidays & Blocked Dates</h3>

      <div style={card()}>
        <h4 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 700, color: B.text, fontFamily: fonts.adminDisplay }}>Add a blocked date</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 10 }}>
          <div>{label("Date")}
            <input style={inputStyle} type="date" value={blockForm.date} onChange={(e) => setBlockForm({ ...blockForm, date: e.target.value })} />
          </div>
          <div>{label("Reason")}
            <input style={inputStyle} value={blockForm.reason} placeholder="e.g. Diwali" onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })} />
          </div>
          <div>{label("Type")}
            <select style={inputStyle} value={blockForm.mode} onChange={(e) => setBlockForm({ ...blockForm, mode: e.target.value })}>
              <option value="full_day">Full day closed</option>
              <option value="half_day">Half day (block a window)</option>
            </select>
          </div>
        </div>

        {blockForm.mode === "half_day" && (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            {label("Block from")}
            <input style={{ width: 120 }} type="time" value={blockForm.block_start_time} onChange={(e) => setBlockForm({ ...blockForm, block_start_time: e.target.value })} />
            <span style={{ color: B.textLight }}>to</span>
            <input style={{ width: 120 }} type="time" value={blockForm.block_end_time} onChange={(e) => setBlockForm({ ...blockForm, block_end_time: e.target.value })} />
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
          {label("Recurring every year?")}
          <select style={{ width: 180 }} value={blockForm.recurring} onChange={(e) => setBlockForm({ ...blockForm, recurring: e.target.value })}>
            <option value="none">No (this date only)</option>
            <option value="yearly">Yes — yearly (same month/day)</option>
          </select>
        </div>

        <Btn variant="primary" small disabled={!isSuperAdmin || blockSaving} onClick={addBlock}>
          {blockSaving ? "Adding…" : buttonAddLabel()}
        </Btn>
      </div>

      <div style={card()}>
        <h4 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: B.text, fontFamily: fonts.adminDisplay }}>
          Blocked dates ({blocks.length})
        </h4>
        {blocks.length === 0 && <p style={{ margin: 0, fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>No blocked dates yet.</p>}
        {blocks.map((b) => (
          <div key={b.date} style={{ display: "flex", alignItems: "center", gap: 12, background: B.cream, borderRadius: 10, padding: "10px 14px", marginBottom: 8, opacity: b.is_active ? 1 : 0.55 }}>
            <span style={{ minWidth: 96, fontWeight: 700, fontSize: 13, color: B.text, fontFamily: fonts.body }}>{b.date}</span>
            <span style={{ flex: 1, fontSize: 13, color: B.textMid, fontFamily: fonts.body }}>
              {b.reason || "—"}
              {b.mode === "half_day" && <em style={{ color: B.orange }}> · {b.block_start_time}–{b.block_end_time}</em>}
              {b.recurring === "yearly" && <em style={{ color: B.textLight }}> · yearly</em>}
            </span>
            <span style={{ fontSize: 11, fontWeight: 600, color: b.is_active ? B.green : B.textLight, fontFamily: fonts.body }}>
              {b.is_active ? "Active" : "Inactive"}
            </span>
            {isSuperAdmin && (
              <span style={{ display: "flex", gap: 6 }}>
                <button type="button" title="Activate / deactivate" onClick={() => toggleBlock(b)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15 }}>
                  {b.is_active ? "⏸" : "▶"}
                </button>
                <button type="button" title="Delete" onClick={() => deleteBlock(b)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15 }}>🗑️</button>
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// label for the add-block button (helper defined after component for readability)
function buttonAddLabel() {
  return "Add blocked date";
}